import store from 'store'
import Vue from 'vue'
import { SEC_TYPE } from 'const/stock'
import { OMNIBUS_ORDER_STATUS, ORDER_STATUS, ORDER_TYPE } from 'const/trade'

const ORDER_ICON_NAME_MAP = {
  // 手动取消
  canceled: {
    icon: 'success',
    name: 'trade.order-cancelled'
  },
  // 订单成交
  filled: {
    icon: 'success',
    name: 'trade.order-filled'
  },
  // 系统取消（包括订单过期撤销）
  rejected: {
    icon: 'warning',
    name: 'trade.order-rejected'
  },
  // 订单已提交/改单
  submitted: {
    icon: 'success',
    name: 'trade.order-placed'
  },
  // 订单部分成交
  'partial-filled': {
    icon: 'success',
    name: 'trade.order-partially-filled'
  },
  // 订单修改被拒（标准账户）
  'modify-reject': {
    icon: 'warning',
    name: 'trade.order-failed'
  }
}

const ORDER_TYPE_NAME_MAP = {
  [ORDER_TYPE.MKT]: 'trade.MKT',
  [ORDER_TYPE.LMT]: 'trade.LMT',
  [ORDER_TYPE.STP]: 'trade.STP',
  [ORDER_TYPE.STP_LMT]: 'trade.STP_LMT',
  [ORDER_TYPE.TRAIL_1]: 'trade.TRAIL_1',
  [ORDER_TYPE.TRAIL_2]: 'trade.TRAIL_2'
}

/**
 * 解析订单状态长连接消息变更通知消息
 * @param {object} order 订单信息
 * @param {boolean} isIb 是否是 ib 账户。不同账户数据结构不一致
 * @returns {{title: '', content: ''}}
 */
export function getOrderStatusNotifyMsg(order, isIb) {
  const isOPT = order.secType === SEC_TYPE.OPT
  const iconName = getOrderNotifyIconName(order, order.messageCode, isIb)
  const orderActionName = Vue.t(
    `trade.order-direction-${order.action.toLowerCase()}`
  )
  const symbol = isOPT ? Vue.filter('formatOptionName')(order, 0) : order.symbol
  const orderTypeName = getOrderTypeName(order)
  const quantity = getOrderNotifyQuantity(order, isIb)
  const price = getOrderNotifyPrice(order)
  const silent =
    [ORDER_STATUS.SUBMITTED, ORDER_STATUS.FILLED].indexOf(order.status) < 0
  if (iconName) {
    const icon = iconName.icon
    const name = Vue.t(iconName.name)

    return {
      title: name,
      icon,
      silent,
      content: `${orderActionName} ${quantity} ${symbol} ${price} (${orderTypeName})`,
      useNative: true,
      onClick: function() {
        store.dispatch('setOrderDetail', order)
      }
    }
  }
}

/**
 * 获取订单操作结果提示
 * @param {string} operation - 操作。新增/修改/取消
 * @param {number} isSuccess - 状态是否成功
 * @param {object} order - 订单信息
 * @returns {{title: *, content: *}}
 */
export function getOrderOperationTips(order, operation, isSuccess) {
  const orderOperationNameMap = {
    create: ['trade.order-placed', 'trade.order-failed'],
    modify: ['trade.order-modify-success', 'trade.order-modify-fail'],
    cancel: [
      'trade.order-cancel-submit-success',
      'trade.order-cancel-submit-fail'
    ]
  }
  const isOPT = order.secType === SEC_TYPE.OPT
  const orderTypeName = getOrderTypeName(order)
  const orderActionName = Vue.t(
    `trade.order-direction-${order.action.toLowerCase()}`
  )
  const symbol = isOPT ? Vue.filter('formatOptionName')(order, 0) : order.symbol
  const title = Vue.t(
    orderOperationNameMap[operation || 'create'][isSuccess ? 0 : 1]
  )
  const quantity = getOrderNotifyQuantity(order, true)
  const price = getOrderNotifyPrice(order)
  const icon = isSuccess ? 'success' : 'warning'
  return {
    title,
    content: `${orderActionName} ${quantity} ${symbol} ${price} (${orderTypeName})`,
    icon,
    useNative: true,
    onClick: function() {
      store.dispatch('setOrderDetail', order)
    }
  }
}

// 获取订单类型名称
function getOrderTypeName(order) {
  let orderType = order.orderType
  if (orderType === ORDER_TYPE.TRAIL) {
    if (order.trailingPercent) {
      orderType = ORDER_TYPE.TRAIL_1
    } else {
      orderType = ORDER_TYPE.TRAIL_2
    }
  }
  return Vue.t(ORDER_TYPE_NAME_MAP[orderType])
}

// 获取消息展示价格
function getOrderNotifyPrice(detail) {
  if (detail.orderType === ORDER_TYPE.LMT) {
    return Vue.t('trade.order-at-price', [detail.limitPrice])
  }
  return ''
}

// 获取提示展示的成交数
function getOrderNotifyQuantity(detail, isIb) {
  // 获取订单总数，成交数，未成交数
  const totalQuantity = detail.totalQuantity || 0
  const filledQuantity = detail.filledQuantity || 0
  const unfilledQuantity = totalQuantity - filledQuantity
  const largeNumber = Vue.filter('largeNumber')
  let quantity = totalQuantity
  // 取消的订单（包括手动取消和系统取消）展示位成交的数量。其余展示全部数量
  if (isIb) {
    if (
      detail.status === ORDER_STATUS.REJECTED ||
      detail.status === ORDER_STATUS.CANCELLED
    ) {
      quantity = unfilledQuantity
    }
  } else {
    if (
      [
        OMNIBUS_ORDER_STATUS.CANCELED,
        OMNIBUS_ORDER_STATUS.REJECTED,
        OMNIBUS_ORDER_STATUS.EXPIRED
      ].includes(detail.status)
    ) {
      quantity = unfilledQuantity
    }
  }
  const unitName = quantity >= 2 ? 'plural-unit' : 'unit'
  const unit =
    detail.secType === SEC_TYPE.OPT
      ? Vue.t(`trade.option-${unitName}`)
      : Vue.t(`trade.${unitName}`)

  return `${largeNumber(quantity)} ${unit}`
}

/**
 * 获取订单消息的图标和标题名
 * @param {object} order - 订单信息
 * @param {string} messageCode - 消息码（标准账户）
 * @param {boolean} isIb - 是否是ib账户
 */
function getOrderNotifyIconName(order, messageCode, isIb) {
  // 成交状态区分全部成交和部分成交
  const isPartialFilled =
    order.filledQuantity && order.filledQuantity < order.totalQuantity
  const submittedKey = isPartialFilled ? 'partial-filled' : 'submitted'
  const filledKey = isPartialFilled ? 'partial-filled' : 'filled'

  let iconNameKey = ''
  if (isIb) {
    iconNameKey = {
      [ORDER_STATUS.SUBMITTED]: submittedKey,
      [ORDER_STATUS.CANCELLED]: 'canceled',
      [ORDER_STATUS.FILLED]: filledKey,
      [ORDER_STATUS.REJECTED]: 'rejected'
    }[order.status]
  } else {
    // new 状态包含多种情况。订单部分提交，提交失败等
    if (order.status === OMNIBUS_ORDER_STATUS.NEW) {
      if (messageCode && messageCode.indexOf('modify.reject') > -1) {
        iconNameKey = 'modify-reject'
      } else {
        iconNameKey = submittedKey
      }
    } else {
      iconNameKey = {
        [OMNIBUS_ORDER_STATUS.CANCELED]: 'canceled',
        [OMNIBUS_ORDER_STATUS.FILLED]: filledKey,
        [OMNIBUS_ORDER_STATUS.REJECTED]: 'rejected',
        [OMNIBUS_ORDER_STATUS.EXPIRED]: 'rejected'
      }[order.status]
    }
  }

  return iconNameKey && ORDER_ICON_NAME_MAP[iconNameKey]
}
